/* Shared, framework-free local checkpoint mechanics. The native image ships an
 * exact copy verified by build-labelstudio-workspace.mjs. No credentials here. */
(function (root) {
  let opened;
  function open() {
    if (!root.indexedDB) return Promise.reject(new Error("Local recovery storage is unavailable"));
    if (!opened) opened = new Promise((resolve, reject) => {
      const request = root.indexedDB.open("celia-recovery-v1", 2);
      request.onupgradeneeded = () => {
        const store = request.result.objectStoreNames.contains("checkpoints") ? request.transaction.objectStore("checkpoints")
          : request.result.createObjectStore("checkpoints", { keyPath: "key" });
        if (!store.indexNames.contains("scope_updated")) store.createIndex("scope_updated", ["scope", "updatedAt"]);
        if (!store.indexNames.contains("actor_updated")) store.createIndex("actor_updated", ["payload.actor_id", "updatedAt"]);
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); opened = undefined; };
        resolve(db);
      };
      request.onerror = () => { opened = undefined; reject(request.error); };
      request.onblocked = () => { opened = undefined; reject(new Error("Close older tabs to enable recovery storage")); };
    });
    return opened;
  }
  async function transact(mode, action) {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("checkpoints", mode);
      let result;
      tx.oncomplete = () => resolve(result);
      tx.onabort = tx.onerror = () => reject(tx.error || new Error("Local checkpoint could not be committed"));
      action(tx.objectStore("checkpoints"), (value) => { result = value; }, tx);
    });
  }
  function get(key) {
    return transact("readonly", (store, done) => {
      const request = store.get(key);
      request.onsuccess = () => done(request.result || null);
    });
  }
  function save(entry) {
    if (!entry.key || !entry.scope || !Number.isSafeInteger(entry.sequence) || entry.sequence < 1)
      return Promise.reject(new Error("Invalid local checkpoint identity"));
    return transact("readwrite", (store, done, tx) => {
      const request = store.get(entry.key);
      request.onsuccess = () => {
        const previous = request.result;
        if (previous && previous.sequence > entry.sequence) { done(previous); return; }
        if (previous && previous.sequence === entry.sequence && JSON.stringify(previous.payload) !== JSON.stringify(entry.payload)) {
          tx.abort(); return;
        }
        const value = { ...previous, ...entry, updatedAt: Date.now(),
          savedSequence: previous?.savedSequence || 0, serverSessionId: entry.serverSessionId || previous?.serverSessionId || null };
        store.put(value);
        done(value);
      };
    });
  }
  function acknowledge(key, sequence, metadata) {
    return transact("readwrite", (store, done) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const value = request.result;
        if (!value) { done(null); return; }
        value.savedSequence = Math.max(value.savedSequence || 0, Math.min(sequence, value.sequence));
        Object.assign(value, metadata || {});
        store.put(value); done(value);
      };
    });
  }
  function list(scope, limit = 20) {
    return transact("readonly", (store, done) => {
      const values = [];
      const range = root.IDBKeyRange.bound([scope, 0], [scope, Number.MAX_SAFE_INTEGER]);
      const request = store.index("scope_updated").openCursor(range, "prev");
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || values.length >= Math.min(limit, 100)) { done(values); return; }
        values.push(cursor.value); cursor.continue();
      };
    });
  }
  function remove(key) {
    return transact("readwrite", (store, done) => { store.delete(key); done(true); });
  }
  function pending(actor, limit = 20) {
    return transact("readonly", (store, done) => {
      const values = [], range = root.IDBKeyRange.bound([actor, 0], [actor, Number.MAX_SAFE_INTEGER]);
      const request = store.index("actor_updated").openCursor(range, "prev");
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || values.length >= Math.min(limit, 100)) { done(values); return; }
        const value = cursor.value;
        if (Number.isSafeInteger(value.payload?.task_id) && (value.savedSequence || 0) < value.sequence
            && (!value.retryAt || value.retryAt <= Date.now())) values.push(value);
        cursor.continue();
      };
    });
  }
  root.CeliaCheckpointStore = Object.freeze({ get, save, acknowledge, list, pending, remove });
})(globalThis);
